import { useState } from "react";
import { Link } from "react-router-dom";
import { cx } from "class-variance-authority";
import { useData } from "@/stores/data";
import {
  LayoutDashboard,
  Wrench,
  Package,
  Building,
  ClipboardList,
  BarChart3,
} from "lucide-react";
import SidebarChevron from "@/assets/icons/SidebarChevron";

export default function SidebarLogisticStaff({ sidebarMinMax = false }) {
  const { setCurrentExport } = useData();
  const [partRepairable, setPartRepairable] = useState(false);
  const [partNonRepairable, setPartNonRepairable] = useState(false);
  const [asset, setAsset] = useState(false);
  const [pr, setPr] = useState(false);
  const [rrt, setRrt] = useState(false);
  const [reportingMenu, setReportingMenu] = useState(false);

  // Helper function to handle accordion behavior - close all other groups when one is opened
  const handleGroupToggle = (groupSetter, currentValue) => {
    // Close all groups
    setPartRepairable(false);
    setPartNonRepairable(false);
    setAsset(false);
    setPr(false);
    setRrt(false);
    setReportingMenu(false);
    // Toggle the target group
    groupSetter(!currentValue);
  };

  return (
    <>
      <Link
        to={"/logistic/dashboard"}
        className={cx(
          "flex",
          "items-center",
          "gap-3",
          "px-4",
          "py-3",
          "rounded-lg",
          "text-gray-700",
          "hover:bg-blue-50",
          "hover:text-blue-700",
          "transition-all",
          "duration-200",
          "group",
          "whitespace-nowrap",
          sidebarMinMax && "justify-center",
        )}
      >
        <LayoutDashboard className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
        {!sidebarMinMax && (
          <span className="font-medium whitespace-nowrap">Dashboard</span>
        )}
      </Link>

      <button
        className={cx(
          "w-full",
          "flex",
          "items-center",
          "justify-between",
          "px-4",
          "py-3",
          "rounded-lg",
          "text-gray-700",
          "hover:bg-gray-100",
          "transition-all",
          "duration-200",
          "group",
        )}
        onClick={() => {
          handleGroupToggle(setPartRepairable, partRepairable);
        }}
      >
        <div className="flex items-center gap-3">
          <Wrench className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
          {!sidebarMinMax && (
            <span className="font-medium whitespace-nowrap">
              Repairable Part inventory
            </span>
          )}
        </div>
        {!sidebarMinMax && (
          <SidebarChevron
            className={`transition-transform duration-200 ${
              partRepairable ? "rotate-180" : ""
            }`}
          />
        )}
      </button>
      {partRepairable && !sidebarMinMax && (
        <div className="ml-6 space-y-1 border-l-2 border-gray-100 pl-4">
          <Link
            to={"/logistic/part_repairable"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Show R Part
          </Link>
          <Link
            to={"/logistic/part_repairable/create"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Receive R Part
          </Link>
          <Link
            to={"/logistic/part_repairable/transfer"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Transfer R Part
          </Link>
          <Link
            to={"/logistic/part_repairable/condition"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Condition Update R Part
          </Link>
        </div>
      )}

      <button
        className={cx(
          "w-full",
          "flex",
          "items-center",
          "justify-between",
          "px-4",
          "py-3",
          "rounded-lg",
          "text-gray-700",
          "hover:bg-gray-100",
          "transition-all",
          "duration-200",
          "group",
        )}
        onClick={() => {
          handleGroupToggle(setPartNonRepairable, partNonRepairable);
        }}
      >
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
          {!sidebarMinMax && (
            <span className="font-medium whitespace-nowrap">
              Non Repairable Part inventory
            </span>
          )}
        </div>
        {!sidebarMinMax && (
          <SidebarChevron
            className={`transition-transform duration-200 ${
              partNonRepairable ? "rotate-180" : ""
            }`}
          />
        )}
      </button>
      {partNonRepairable && !sidebarMinMax && (
        <div className="ml-6 space-y-1 border-l-2 border-gray-100 pl-4">
          <Link
            to={"/logistic/part_non_repairable"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Show NR Part
          </Link>
          <Link
            to={"/logistic/part_non_repairable/create"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Receive NR Part
          </Link>
          <Link
            to={"/logistic/part_non_repairable/transfer"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Transfer NR Part
          </Link>
          <Link
            to={"/logistic/part_non_repairable/condition"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Condition Update NR Part
          </Link>
        </div>
      )}

      <button
        className={cx(
          "w-full",
          "flex",
          "items-center",
          "justify-between",
          "px-4",
          "py-3",
          "rounded-lg",
          "text-gray-700",
          "hover:bg-gray-100",
          "transition-all",
          "duration-200",
          "group",
        )}
        onClick={() => {
          handleGroupToggle(setAsset, asset);
        }}
      >
        <div className="flex items-center gap-3">
          <Building className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
          {!sidebarMinMax && (
            <span className="font-medium whitespace-nowrap">
              Asset inventory
            </span>
          )}
        </div>
        {!sidebarMinMax && (
          <SidebarChevron
            className={`transition-transform duration-200 ${
              asset ? "rotate-180" : ""
            }`}
          />
        )}
      </button>
      {asset && !sidebarMinMax && (
        <div className="ml-6 space-y-1 border-l-2 border-gray-100 pl-4">
          <Link
            to={"/logistic/asset"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Show Asset
          </Link>
          <Link
            to={"/logistic/asset/create"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Receive Asset
          </Link>
          <Link
            to={"/logistic/asset/transfer"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Transfer Asset
          </Link>
          <Link
            to={"/logistic/asset/condition"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Condition Update Asset
          </Link>
        </div>
      )}

      <button
        className={cx(
          "w-full",
          "flex",
          "items-center",
          "justify-between",
          "px-4",
          "py-3",
          "rounded-lg",
          "text-gray-700",
          "hover:bg-gray-100",
          "transition-all",
          "duration-200",
          "group",
        )}
        onClick={() => {
          handleGroupToggle(setPr, pr);
        }}
      >
        <div className="flex items-center gap-3">
          <ClipboardList className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
          {!sidebarMinMax && (
            <span className="font-medium whitespace-nowrap">Part Request</span>
          )}
        </div>
        {!sidebarMinMax && (
          <SidebarChevron
            className={`transition-transform duration-200 ${
              pr ? "rotate-180" : ""
            }`}
          />
        )}
      </button>
      {pr && !sidebarMinMax && (
        <div className="ml-6 space-y-1 border-l-2 border-gray-100 pl-4">
          <Link
            to={"/logistic/pr/all"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            All
          </Link>
          <Link
            to={"/logistic/pr/unclosed"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Unclosed PRT
          </Link>
          <Link
            to={"/logistic/pr/pending"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Pending PRT
          </Link>
          <Link
            to={"/logistic/pr/closed"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Closed PRT
          </Link>
          <Link
            to={"/logistic/part-returned/prt/all"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Part Returned PRT
          </Link>
        </div>
      )}

      <button
        className={cx(
          "w-full",
          "flex",
          "items-center",
          "justify-between",
          "px-4",
          "py-3",
          "rounded-lg",
          "text-gray-700",
          "hover:bg-gray-100",
          "transition-all",
          "duration-200",
          "group",
        )}
        onClick={() => {
          handleGroupToggle(setRrt, rrt);
        }}
      >
        <div className="flex items-center gap-3">
          <Wrench className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
          {!sidebarMinMax && (
            <span className="font-medium whitespace-nowrap">
              Repair Request
            </span>
          )}
        </div>
        {!sidebarMinMax && (
          <SidebarChevron
            className={`transition-transform duration-200 ${
              rrt ? "rotate-180" : ""
            }`}
          />
        )}
      </button>
      {rrt && !sidebarMinMax && (
        <div className="ml-6 space-y-1 border-l-2 border-gray-100 pl-4">
          <Link
            to={"/logistic/rr/all"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            All
          </Link>
          <Link
            to={"/logistic/rr/create"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Create
          </Link>
          <Link
            to={"/logistic/part-returned/rrt/all"}
            className={cx(
              "block",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
              "whitespace-nowrap",
            )}
          >
            Part Returned RRT
          </Link>
        </div>
      )}

      <button
        className={cx(
          "w-full",
          "flex",
          "items-center",
          "justify-between",
          "px-4",
          "py-3",
          "rounded-lg",
          "text-gray-700",
          "hover:bg-gray-100",
          "transition-all",
          "duration-200",
          "group",
        )}
        onClick={() => {
          handleGroupToggle(setReportingMenu, reportingMenu);
        }}
      >
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
          {!sidebarMinMax && (
            <span className="font-medium whitespace-nowrap">Reporting</span>
          )}
        </div>
        {!sidebarMinMax && (
          <SidebarChevron
            className={`transition-transform duration-200 ${
              reportingMenu ? "rotate-180" : ""
            }`}
          />
        )}
      </button>
      {reportingMenu && !sidebarMinMax && (
        <div className="ml-6 space-y-1 border-l-2 border-gray-100 pl-4">
          <button
            onClick={() => {
              setCurrentExport("part repairable");
            }}
            className={cx(
              "block",
              "w-full",
              "text-left",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
            )}
          >
            Part Repairable
          </button>
          <button
            onClick={() => {
              setCurrentExport("part non repairable");
            }}
            className={cx(
              "block",
              "w-full",
              "text-left",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
            )}
          >
            Part Non Repairable
          </button>
          <button
            onClick={() => {
              setCurrentExport("asset");
            }}
            className={cx(
              "block",
              "w-full",
              "text-left",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
            )}
          >
            Asset
          </button>
          <button
            onClick={() => {
              setCurrentExport("prt reporting");
            }}
            className={cx(
              "block",
              "w-full",
              "text-left",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
            )}
          >
            PRT Reporting
          </button>
          <button
            onClick={() => {
              setCurrentExport("rrt reporting");
            }}
            className={cx(
              "block",
              "w-full",
              "text-left",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
            )}
          >
            RRT Reporting
          </button>
          <button
            onClick={() => {
              setCurrentExport("part usage");
            }}
            className={cx(
              "block",
              "w-full",
              "text-left",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
            )}
          >
            Part Usage
          </button>
          <Link
            to="/logistic/stock"
            className={cx(
              "flex",
              "items-center",
              "gap-3",
              "px-3",
              "py-2",
              "text-sm",
              "text-gray-600",
              "hover:text-blue-600",
              "hover:bg-blue-50",
              "rounded-md",
              "transition-all",
              "duration-200",
            )}
          >
            Stock Monitoring
          </Link>
        </div>
      )}
    </>
  );
}
